import {
  Group,
  Button,
  Text,
  Stack,
  Box,
  Card,
  Table,
  ThemeIcon,
  ScrollArea,
} from '@mantine/core';
import {
  Check,
  ChevronLeft,
  Info,
  Users,
  Grid,
} from 'react-feather';
import type { CSVImportPreviewRow, CSVImportResult } from '../../../../types';
import { REQUIRED_FIELDS } from './importConstants';
import type { PreviewStats, ReviewColumn } from './importTypes';
import classes from '../ImportParticipantsModal.module.css';

interface ReviewStepProps {
  previewRows: CSVImportPreviewRow[];
  previewStats: PreviewStats;
  reviewColumns: ReviewColumn[];
  importResult: CSVImportResult | null;
  emptyOptionalFieldsCount: number;
  importing: boolean;
  canProceed: () => boolean;
  cohortName: string;
  onImport: () => Promise<void>;
  onViewParticipants: () => void;
  onBack: () => void;
  getCsvValue: (rowIndex: number, csvColumn: string) => string | undefined;
}

export function ReviewStep({
  previewRows,
  previewStats,
  reviewColumns,
  importResult,
  emptyOptionalFieldsCount,
  importing,
  canProceed,
  cohortName,
  onImport,
  onViewParticipants,
  onBack,
  getCsvValue,
}: ReviewStepProps) {
  if (importResult) {
    return (
      /* Confirmation View */
      <Stack align="center" gap="lg" py="lg">
        {/* Success Indicator */}
        <Box className={classes.successIcon}>
          <Check size={48} color="var(--mantine-color-medleyGreen-6)" />
        </Box>

        <Text size="xl" fw={700}>Participants Imported!</Text>
        <Text c="dimmed" ta="center">
          {importResult.imported + importResult.updated} participants have been successfully added to
          <br />
          <Text span fw={600}>{cohortName}</Text>
        </Text>

        {/* Statistics Card */}
        <Card className={classes.statsCard} p="lg">
          <Group justify="center" gap={60}>
            <Stack align="center" gap={4}>
              <Text size="xl" fw={700}>{importResult.imported + importResult.updated}</Text>
              <Text size="xs" c="dimmed" tt="uppercase">Imported</Text>
            </Stack>
            <Stack align="center" gap={4}>
              <Text size="xl" fw={700}>0</Text>
              <Text size="xs" c="dimmed" tt="uppercase">Groups</Text>
            </Stack>
            <Stack align="center" gap={4}>
              <Text size="xl" fw={700}>{importResult.imported + importResult.updated}</Text>
              <Text size="xs" c="dimmed" tt="uppercase">Unassigned</Text>
            </Stack>
          </Group>
        </Card>

        {/* Recommended Next Steps */}
        <Box w="100%">
          <Text size="xs" c="dimmed" tt="uppercase" mb="sm" style={{ letterSpacing: '0.5px' }}>
            Recommended Next Steps
          </Text>
          <Card withBorder p="md">
            <Group gap="md">
              <ThemeIcon size={40} radius="xl" variant="light" color="gray">
                <Users size={20} />
              </ThemeIcon>
              <Box>
                <Text fw={500}>Collect participant information</Text>
                <Text size="sm" c="dimmed">Send an intake form to gather additional details</Text>
              </Box>
            </Group>
          </Card>
        </Box>

        {/* Footer Button */}
        <Button
          fullWidth
          size="md"
          leftSection={<Grid size={16} />}
          onClick={onViewParticipants}
        >
          View Participants
        </Button>
      </Stack>
    );
  }

  return (
    /* Review View */
    <Stack gap="md">
      {/* Summary Display */}
      <Group justify="flex-end">
        <Group gap="xs">
          <Text size="sm">
            <Text span fw={600}>{previewStats.total}</Text> total
          </Text>
          <Text size="sm" c="dimmed">&middot;</Text>
          <Text size="sm" c="medleyGreen">
            <Text span fw={600}>{previewStats.valid}</Text> valid
          </Text>
          {previewStats.warnings > 0 && (
            <>
              <Text size="sm" c="dimmed">&middot;</Text>
              <Text size="sm" c="warningAmber">
                <Text span fw={600}>{previewStats.warnings}</Text> warnings
              </Text>
            </>
          )}
        </Group>
      </Group>

      {/* Data Table */}
      <ScrollArea.Autosize mah={400}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={50}>#</Table.Th>
              {reviewColumns.map((col) => (
                <Table.Th key={col.key}>
                  {col.label.toUpperCase()}
                  {col.required && <Text span c="red"> *</Text>}
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {previewRows.map((row, index) => (
              <Table.Tr key={row.rowIndex}>
                <Table.Td>
                  <Text size="sm" c="dimmed">{index + 1}</Text>
                </Table.Td>
                {reviewColumns.map((col) => {
                  // For custom fields, get value from raw CSV; for standard fields, use row.data
                  const value = col.isCustom && col.csvColumn
                    ? getCsvValue(row.rowIndex, col.csvColumn)
                    : (row.data[col.key as keyof typeof row.data] as string | undefined);
                  const isEmpty = !value || value.trim() === '';
                  const isRequired = REQUIRED_FIELDS.includes(col.key);
                  const hasWarning = isEmpty && !isRequired && !col.isCustom;

                  return (
                    <Table.Td key={col.key}>
                      {isEmpty ? (
                        hasWarning ? (
                          <Box
                            style={{
                              border: '1px dashed #D97706',
                              borderRadius: 4,
                              padding: '6px 12px',
                              minWidth: 60,
                            }}
                          >
                            <Text size="sm" c="dimmed">&mdash;</Text>
                          </Box>
                        ) : (
                          <Text size="sm" c="dimmed">&mdash;</Text>
                        )
                      ) : (
                        <Text size="sm">{value}</Text>
                      )}
                    </Table.Td>
                  );
                })}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea.Autosize>

      {/* Footer with warning */}
      <Group justify="space-between" align="center">
        <Group gap="md" align="center">
          <Button
            variant="default"
            leftSection={<ChevronLeft size={16} />}
            onClick={onBack}
          >
            Back
          </Button>

          {emptyOptionalFieldsCount > 0 && (
            <Group gap="xs">
              <Info size={14} color="#D97706" />
              <Text size="sm" style={{ color: '#D97706' }}>
                {emptyOptionalFieldsCount} rows have empty optional fields
              </Text>
            </Group>
          )}
        </Group>

        <Button
          onClick={onImport}
          loading={importing}
          disabled={!canProceed()}
        >
          Import {previewStats.valid} Participants
        </Button>
      </Group>
    </Stack>
  );
}
